import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Alert, Spinner } from 'react-bootstrap';
import Cookies from 'js-cookie';
import { 
  FaBook, 
  FaUser, 
  FaClock, 
  FaCalendarAlt, 
  FaGraduationCap, 
  FaAward,
  FaBookOpen,
  FaStar,
  FaExclamationTriangle,
  FaInfoCircle,
  FaIdCard,
  FaUserGraduate
} from 'react-icons/fa';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const CourseDetail = () => {
  const [selectedCourseId, setSelectedCourseId] = useState("8");
  const [courseData, setCourseData] = useState(null);
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        setLoading(true);
        
        const token = Cookies.get('token');
        
        const userDataString = localStorage.getItem('user');
        const userData = userDataString ? JSON.parse(userDataString) : null;
        const studentId = userData?.studentId;
        
        setStudentData(userData);
        
        if (!token) {
          throw new Error('No authentication token found. Please login again.');
        }
        
        const response = await fetch(
          `${BASE_URL}/academic/getCurrentCourses?studentId=${studentId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json',
            }
          }
        );
        
        if (response.status === 401) {
          Cookies.remove('token');
          Cookies.remove('userRole');
          localStorage.removeItem('user');
          throw new Error('Session expired. Please login again.');
        }
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.status === "1") {
          setCourseData(data);
          if (data.courses && data.courses.length > 0) {
            setSelectedCourseId(data.courses[0].id.toString());
          }
        } else {
          throw new Error(data.message || 'Failed to load course data');
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching course data:', err);
        
        if (err.message.includes('Unauthorized') || err.message.includes('Session expired') || err.message.includes('authentication token')) {
          setTimeout(() => {
            window.location.href = '/student/login';
          }, 2000);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCourseData();
  }, []);

  const handleCourseSelection = (courseId) => {
    setSelectedCourseId(courseId.toString());
    console.log('Course selected:', courseId);
  };

  if (loading) {
    return (
      <Container fluid className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <p className="mt-3 text-muted">Loading course details...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="mt-4">
        <Alert variant="danger" className="d-flex align-items-center">
          <FaExclamationTriangle className="me-2" />
          <div>
            <Alert.Heading>Error Loading Course</Alert.Heading>
            <p className="mb-0">{error}</p>
            <hr />
            <div className="d-flex justify-content-end">
              <button 
                className="btn btn-outline-danger me-2" 
                onClick={() => window.location.reload()}
              >
                Try Again
              </button>
              {(error.includes('Session expired') || error.includes('authentication token')) && (
                <button 
                  className="btn btn-primary" 
                  onClick={() => window.location.href = '/student/login'}
                >
                  Login Again
                </button>
              )}
            </div>
          </div>
        </Alert>
      </Container>
    );
  }

  const selectedCourse = courseData?.courses?.find(course => course.id.toString() === selectedCourseId) || courseData?.courses?.[0];

  if (!selectedCourse) {
    return (
      <Container fluid className="mt-4">
        <Alert variant="warning" className="d-flex align-items-center">
          <FaInfoCircle className="me-2" />
          <div>
            <Alert.Heading>Course Not Found</Alert.Heading>
            <p className="mb-0">The requested course could not be found.</p>
          </div>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <Card className="shadow-lg mb-4 overflow-hidden" style={{ 
        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)',
        border: 'none',
        borderRadius: '20px'
      }}>
        <Card.Body className="p-0 position-relative">
          <div 
            className="position-absolute w-100 h-100"
            style={{
              background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
              opacity: 0.3
            }}
          />
          
          <div className="position-relative text-white p-4">
            <Row className="align-items-center mb-4">
              <Col>
                <div className="d-flex align-items-center mb-3">
                  <div 
                    className="bg-white bg-opacity-20 rounded-circle p-3 me-4"
                    style={{ 
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.2)'
                    }}
                  >
                    <FaUserGraduate size={28} className="text-dark" />
                  </div>
                  <div>
                    <h1 className="mb-1 fw-bold display-6">Student Dashboard</h1>
                    <p className="mb-0 fs-4 fw-light" style={{ opacity: 0.9 }}>
                      Welcome back, <span className="fw-semibold">{studentData?.name || 'Student'}</span>
                    </p>
                  </div>
                </div>
              </Col>
            </Row>
            
            <Row className="g-3 mb-4">
              <Col lg={4} md={6}>
                <div 
                  className="bg-white rounded-4 p-4 h-100 shadow-sm"
                  style={{ 
                    transition: 'transform 0.2s ease',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <div className="d-flex align-items-center mb-3">
                    <div className="bg-primary bg-opacity-10 rounded-3 p-2 me-3">
                      <FaUser size={18} className="text-primary" />
                    </div>
                    <div>
                      <small className="text-muted text-uppercase fw-medium" style={{ fontSize: '0.75rem' }}>
                        Student Name
                      </small>
                      <h5 className="mb-0 fw-bold text-dark">{studentData?.name || 'N/A'}</h5>
                    </div>
                  </div>
                </div>
              </Col>
              
              <Col lg={4} md={6}>
                <div 
                  className="bg-white rounded-4 p-4 h-100 shadow-sm"
                  style={{ 
                    transition: 'transform 0.2s ease',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <div className="d-flex align-items-center mb-3">
                    <div className="bg-primary bg-opacity-10 rounded-3 p-2 me-3">
                      <FaIdCard size={18} className="text-primary" />
                    </div>
                    <div>
                      <small className="text-muted text-uppercase fw-medium" style={{ fontSize: '0.75rem' }}>
                        Student ID
                      </small>
                      <h5 className="mb-0 fw-bold text-dark">{studentData?.studentId || 'N/A'}</h5>
                    </div>
                  </div>
                </div>
              </Col>
              
              <Col lg={4} md={12}>
                <div 
                  className="bg-white rounded-4 p-4 h-100 shadow-sm"
                  style={{ 
                    transition: 'transform 0.2s ease',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <div className="d-flex align-items-center mb-3">
                    <div className="bg-primary bg-opacity-10 rounded-3 p-2 me-3">
                      <FaGraduationCap size={18} className="text-primary" />
                    </div>
                    <div>
                      <small className="text-muted text-uppercase fw-medium" style={{ fontSize: '0.75rem' }}>
                        Program
                      </small>
                      <h6 className="mb-0 fw-bold text-dark text-truncate">
                        {studentData?.programme || courseData?.programmes || 'N/A'}
                      </h6>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            <Row className="g-3">
              <Col sm={6} lg={3}>
                <div className="text-center p-3">
                  <div className="bg-white bg-opacity-10 rounded-circle p-3 d-inline-flex mb-3">
                    <FaCalendarAlt size={20} className="text-white" />
                  </div>
                  <div>
                    <small className="text-white-50 text-uppercase fw-medium d-block" style={{ fontSize: '0.7rem' }}>
                      Academic Year
                    </small>
                    <div className="fw-bold text-white fs-5">
                      {studentData?.academicyear || courseData?.academicyear || 'N/A'}
                    </div>
                  </div>
                </div>
              </Col>
              
              <Col sm={6} lg={3}>
                <div className="text-center p-3">
                  <div className="bg-white bg-opacity-10 rounded-circle p-3 d-inline-flex mb-3">
                    <FaAward size={20} className="text-white" />
                  </div>
                  <div>
                    <small className="text-white-50 text-uppercase fw-medium d-block" style={{ fontSize: '0.7rem' }}>
                      Program Year
                    </small>
                    <div className="fw-bold text-white fs-5">
                      Year {studentData?.programmeyear || courseData?.programmeyear || 'N/A'}
                    </div>
                  </div>
                </div>
              </Col>
              
              <Col sm={6} lg={3}>
                <div className="text-center p-3">
                  <div className="bg-white bg-opacity-10 rounded-circle p-3 d-inline-flex mb-3">
                    <FaBookOpen size={20} className="text-white" />
                  </div>
                  <div>
                    <small className="text-white-50 text-uppercase fw-medium d-block" style={{ fontSize: '0.7rem' }}>
                      Semester
                    </small>
                    <div className="fw-bold text-white fs-5">
                      Semester {studentData?.semester || courseData?.semester || 'N/A'}
                    </div>
                  </div>
                </div>
              </Col>
              
              <Col sm={6} lg={3}>
                <div className="text-center p-3">
                  <div className="bg-white bg-opacity-10 rounded-circle p-3 d-inline-flex mb-3">
                    <FaBook size={20} className="text-white" />
                  </div>
                  <div>
                    <small className="text-white-50 text-uppercase fw-medium d-block" style={{ fontSize: '0.7rem' }}>
                      Total Courses
                    </small>
                    <div className="fw-bold text-white fs-5">
                      {courseData?.courses?.length || 0}
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Card.Body>
      </Card>

      <Row>
        <Col lg={8}>
          <Card className="shadow-sm h-100">
            <Card.Header className="bg-light d-flex align-items-center">
              <div className="bg-primary rounded p-2 me-3">
                <FaBookOpen size={20} className="text-white" />
              </div>
              <div>
                <h4 className="mb-1 fw-bold text-dark">{selectedCourse.exName}</h4>
                <Badge bg="primary" className="fw-normal">{selectedCourse.exCoursesCode}</Badge>
              </div>
            </Card.Header>

            <Card.Body className="p-4">
              <Row className="mb-4">
                <Col md={6}>
                  <div className="d-flex align-items-center p-3 bg-light rounded">
                    <FaUser size={18} className="text-muted me-3" />
                    <div>
                      <small className="text-muted d-block">Discipline</small>
                      <span className="fw-semibold">{selectedCourse.discipline}</span>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="d-flex align-items-center p-3 bg-light rounded">
                    <FaClock size={18} className="text-muted me-3" />
                    <div>
                      <small className="text-muted d-block">Credit Hours</small>
                      <span className="fw-semibold">{selectedCourse.exCreditHourTitle}</span>
                    </div>
                  </div>
                </Col>
              </Row>

              <div className="d-flex align-items-center mb-4">
                <FaStar 
                  size={16} 
                  className={`me-2 ${selectedCourse.isElective === "1" ? "text-warning" : "text-muted"}`} 
                />
                <Badge 
                  bg={selectedCourse.isElective === "1" ? "warning" : "success"}
                  className="fw-normal"
                >
                  {selectedCourse.isElective === "1" ? "Elective Course" : "Core Course"}
                </Badge>
              </div>

              <Alert variant="info" className="mb-4">
                <FaInfoCircle className="me-2" />
                <strong>Course Information</strong>
                <p className="mb-0 mt-2">
                  This course is part of your {studentData?.programme || courseData?.programmes || 'current'} program and falls under the {selectedCourse.discipline} discipline. 
                  It carries {selectedCourse.exCreditHourTitle} credit hours and is classified as a {selectedCourse.isElective === "1" ? "elective" : "core"} course.
                </p>
              </Alert>

              <Row>
                <Col md={4} className="text-center mb-3">
                  <Card className="border-0 bg-primary bg-opacity-10">
                    <Card.Body className="py-3">
                      <FaBook size={24} className="text-primary mb-2" />
                      <div className="small text-primary fw-medium">Course ID</div>
                      <div className="fs-5 fw-bold text-dark">{selectedCourse.id}</div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4} className="text-center mb-3">
                  <Card className="border-0 bg-success bg-opacity-10">
                    <Card.Body className="py-3">
                      <FaAward size={24} className="text-success mb-2" />
                      <div className="small text-success fw-medium">Credit ID</div>
                      <div className="fs-5 fw-bold text-dark">{selectedCourse.exCreditHourId}</div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4} className="text-center mb-3">
                  <Card className="border-0 bg-warning bg-opacity-10">
                    <Card.Body className="py-3">
                      <FaGraduationCap size={24} className="text-warning mb-2" />
                      <div className="small text-warning fw-medium">Discipline ID</div>
                      <div className="fs-5 fw-bold text-dark">{selectedCourse.disciplineId}</div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="shadow-sm h-100">
            <Card.Header className="bg-light">
              <h5 className="mb-1 fw-semibold text-dark">All Enrolled Courses</h5>
              <small className="text-muted">Semester {studentData?.semester || courseData?.semester || 'N/A'} Courses</small>
            </Card.Header>

            <Card.Body className="p-0" style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {courseData?.courses?.map((course) => (
                <div 
                  key={course.id}
                  className={`p-3 border-bottom cursor-pointer ${
                    course.id.toString() === selectedCourseId 
                      ? 'bg-primary bg-opacity-10 border-primary border-start border-3' 
                      : ''
                  }`}
                  style={{ 
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onClick={() => handleCourseSelection(course.id)}
                  onMouseEnter={(e) => {
                    if (course.id.toString() !== selectedCourseId) {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (course.id.toString() !== selectedCourseId) {
                      e.currentTarget.style.backgroundColor = '';
                    }
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h6 className="mb-0 fw-semibold text-truncate me-2" style={{ fontSize: '0.9rem' }}>
                      {course.exName}
                    </h6>
                    <Badge 
                      bg={course.isElective === "1" ? "warning" : "success"} 
                      className="flex-shrink-0"
                      style={{ fontSize: '0.7rem' }}
                    >
                      {course.isElective === "1" ? "Elective" : "Core"}
                    </Badge>
                  </div>
                  <div className="text-primary fw-medium small mb-1">{course.exCoursesCode}</div>
                  <div className="d-flex justify-content-between align-items-center small text-muted">
                    <span className="text-truncate me-2">{course.discipline}</span>
                    <span className="flex-shrink-0">{course.exCreditHourTitle} Credits</span>
                  </div>
                </div>
              )) || []}
            </Card.Body>

            <Card.Footer className="bg-light text-center">
              <div className="small">
                <strong>Total: {courseData?.courses?.length || 0}</strong> courses
              </div>
              <div className="small text-muted">
                {courseData?.courses?.filter(c => c.isElective === "0").length || 0} Core • {courseData?.courses?.filter(c => c.isElective === "1").length || 0} Elective
              </div>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default CourseDetail;